/**
 * 20260923 - HOST OS FLAGS: root-level CSS hooks for facts about the host OS VERSION.
 *
 * The model is theme/macTitlebarInset.ts: a fact Unity puts on the URL, read once before first
 * render and published on <html>, so the CSS that needs it is right on the very first frame and no
 * component has to know where it came from. There the fact is a measured inset (a custom property);
 * here it is a yes/no, so it is an attribute a selector can gate on.
 *
 * ONE FLAG TODAY, `data-ipad-legacy-multitasking`: an iPad on iPadOS 15-18, where the system draws
 * the top-centre "…" multitasking pill over the app window. The decision is
 * ipadLegacyMultitaskingApplies (platform/inputCapabilities.ts), which is where the version range and
 * the "absent osMajor = status quo" rule are argued; this file only publishes the answer.
 *
 * WHICH ROUTES CARRY THE PARAMS: every route that appends TimelineWebViewPanel
 * .GetDeviceInputQueryFragment() — the board, ?page=projects, Screenplay Manager and the Character
 * Creator. NOT the iOS purchasing / sign-in routes, which send no `platform=` at all: there the flag
 * is simply never set, which is correct, because nothing on those pages reads it.
 *
 * WHO CONSUMES IT: SceneBoardPage.module.css — the top-level
 * `:global([data-ipad-legacy-multitasking]) .shell` padding-top rule that clears the pill, and the
 * `:global(:root:not([data-ipad-legacy-multitasking]))` gate on the tablet block's 72px Home
 * clearance, which exists for the iPadOS 26+ window-controls cluster and is dead weight here.
 */
import { getHostInput, ipadLegacyMultitaskingApplies } from './inputCapabilities';

/** The attribute set on <html>. Present-or-absent; its value is always ''. */
export const IPAD_LEGACY_MULTITASKING_ATTR = 'data-ipad-legacy-multitasking';

/**
 * Reads the host facts from the URL and SETS or REMOVES each flag — both directions, so the call is
 * idempotent and a re-run after the URL changed cannot leave a stale flag behind. Never throws: a
 * bad URL must leave the bar at its status-quo layout rather than blocking boot.
 */
export function applyHostOsFlags(): void {
  try {
    const root = document.documentElement;
    if (ipadLegacyMultitaskingApplies(getHostInput())) {
      root.setAttribute(IPAD_LEGACY_MULTITASKING_ATTR, '');
    } else {
      root.removeAttribute(IPAD_LEGACY_MULTITASKING_ATTR);
    }
  } catch {
    // Reading the URL must never be able to block boot.
  }
}
